import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Database } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import TaxonomyPicker from "@/components/TaxonomyPicker";
import TagsInput from "@/components/TagsInput";

const EMPTY = {
  name: "", taxonomy_id: null, description: "",
  source_name: "", source_url: "", download_url: "",
  license: "Public", formats: [], size: "", citation: "", tags: [],
};

export default function AdminDatasetsPage() {
  const [items, setItems] = useState([]);
  const pager = usePager(items, 20);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/datasets"); setItems(r.data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing === "new") await api.post("/admin/datasets", form);
      else await api.put(`/admin/datasets/${editing}`, form);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this dataset?")) return;
    await api.delete(`/admin/datasets/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-datasets-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DATASET REPOSITORY</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Manage <em className="italic text-kb-primary">public datasets</em></h1>
        </div>
        <button onClick={() => { setEditing("new"); setForm(EMPTY); }} className="kb-btn-primary text-sm" data-testid="dataset-new-btn">
          <Plus className="w-3.5 h-3.5" /> New dataset
        </button>
      </div>

      {editing && (
        <div className="mt-6 kb-card p-6" data-testid="dataset-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{editing === "new" ? "NEW DATASET" : "EDITING"}</div>
            <button onClick={() => setEditing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="kb-input text-sm sm:col-span-2" placeholder="Dataset name *"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dataset-name-input" />
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Domain hierarchy</label>
              <TaxonomyPicker domain="datasets" value={form.taxonomy_id} onChange={(id) => setForm({ ...form, taxonomy_id: id })} />
            </div>
            <textarea rows={3} className="kb-input text-sm sm:col-span-2 resize-none" placeholder="Description"
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input className="kb-input text-sm" placeholder="Source name (e.g. WHO)"
              value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })} />
            <input className="kb-input text-sm" placeholder="License (CC-BY, Public, etc.)"
              value={form.license} onChange={(e) => setForm({ ...form, license: e.target.value })} />
            <input className="kb-input text-sm" placeholder="Source URL" value={form.source_url}
              onChange={(e) => setForm({ ...form, source_url: e.target.value })} />
            <input className="kb-input text-sm" placeholder="Direct download URL (optional)" value={form.download_url}
              onChange={(e) => setForm({ ...form, download_url: e.target.value })} />
            <input className="kb-input text-sm" placeholder="Formats (CSV, JSON, NetCDF)"
              value={(form.formats || []).join(", ")}
              onChange={(e) => setForm({ ...form, formats: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            <input className="kb-input text-sm" placeholder="Size (e.g. 1.2 GB)"
              value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} />
            <input className="kb-input text-sm sm:col-span-2" placeholder="Citation"
              value={form.citation} onChange={(e) => setForm({ ...form, citation: e.target.value })} />
            <div className="sm:col-span-2">
              <TagsInput
                value={form.tags || []}
                onChange={(tags) => setForm({ ...form, tags })}
                placeholder="Tags (type and press Enter)"
                testId="dataset-tags"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="kb-btn-primary text-sm" data-testid="dataset-save-btn">Save dataset</button>
            <button onClick={() => setEditing(null)} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No datasets yet.</div>
        ) :
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((d) => (
            <div key={d.id} className="kb-card p-5" data-testid={`admin-dataset-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Database className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2 line-clamp-1">
                    {(d.path || []).join(" › ") || "—"}
                  </div>
                  <h3 className="font-serif text-lg text-kb-ink mt-1 leading-snug">{d.name}</h3>
                  <div className="kb-mono text-[10px] uppercase tracking-wider mt-2 text-kb-ink-light">
                    {d.source_name} · {d.license} · {d.size}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => { setEditing(d.id); setForm({ ...EMPTY, ...d }); }} className="p-1.5 text-kb-ink-muted hover:text-kb-primary" data-testid={`admin-dataset-${d.id}-edit`}><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(d.id)} className="p-1.5 text-rose-600 hover:text-rose-700" data-testid={`admin-dataset-${d.id}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>}
      <Pager {...pager} label="datasets" testId="admin-datasets-pager" />
    </div>
  );
}